import { Alert, Col, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import FormField, { OptionType } from '../form/FormField';
import { FormikState } from 'formik/dist/types';
import { ApiOrganisation, ApiOrganisationCurrencyEnum } from '../api';
import { symbolOfCurrency } from './helper';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../common/errors/helpers';
import { activateOrganisation, deactivateOrganisation, importOrganisationConfig } from './organisationActions';
import { FileUpload, SalvButton, SalvModal, SalvRadio, SalvRadioGroup, Size } from '@salvapp/ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFileArrowUp, faPlus, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import './Organisations.scss';

export type OrganisationModalSource = 'empty' | 'demo' | 'import';

const SOURCE_EMPTY: OrganisationModalSource = 'empty';
const SOURCE_DEMO: OrganisationModalSource = 'demo';
const SOURCE_IMPORT: OrganisationModalSource = 'import';

const SOURCE_OPTIONS: {
  value: OrganisationModalSource;
  title: string;
  description: string;
  icon: IconDefinition;
}[] = [
  { value: SOURCE_EMPTY, title: 'Start empty', description: 'Configure from scratch.', icon: faFile },
  {
    value: SOURCE_DEMO,
    title: 'Demo preset',
    description: 'Seed with a Salv-maintained template.',
    icon: faWandMagicSparkles,
  },
  {
    value: SOURCE_IMPORT,
    title: 'Import bundle',
    description: 'Upload a JSON export from another organisation.',
    icon: faFileArrowUp,
  },
];

export const validationSchema = Yup.object().shape({
  name: Yup.string().trim().min(1, 'Required').max(255, 'Too long').required('Required'),
  currency: Yup.object().shape({
    label: Yup.string().required('Required'),
    key: Yup.string().required('Required'),
    value: Yup.string().required('Required'),
  }),
  source: Yup.mixed<OrganisationModalSource>().oneOf([SOURCE_EMPTY, SOURCE_DEMO, SOURCE_IMPORT]).required(),
  file: Yup.mixed<File>().when('source', {
    is: SOURCE_IMPORT,
    then: (schema) =>
      schema
        .required('Required')
        .test('size', 'Bundle must be 10 MB or smaller', (file) => !file || (file as File).size <= 10 * 1024 * 1024)
        .test('type', 'Must be a JSON bundle', (file) => {
          const f = file as File | undefined;
          if (!f) return true;
          return f.type === 'application/json' || f.name.toLowerCase().endsWith('.json');
        }),
    otherwise: (schema) => schema.nullable(),
  }),
});

export interface OrganisationModalProps {
  organisation?: ApiOrganisation;
  onChange: (values?: OrganisationModalValues) => Promise<void>;
}

export interface OrganisationModalValues {
  id?: number;
  name: string;
  currency: OptionType;
  source: OrganisationModalSource;
  file: File | null;
}

const canCreate = (values: OrganisationModalValues): boolean =>
  values.name.trim().length >= 2 && (values.source !== SOURCE_IMPORT || values.file !== null);

export const OrganisationModal = ({ organisation, onChange }: OrganisationModalProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);

  const convertForSelect = (type: string): OptionType => {
    return { label: symbolOfCurrency[type], key: type, value: type };
  };

  const currencyType = Object.keys(ApiOrganisationCurrencyEnum).map((type) => convertForSelect(type));

  const initialValues: OrganisationModalValues =
    organisation !== undefined
      ? {
          ...organisation,
          currency: convertForSelect(organisation.currency),
          source: SOURCE_EMPTY,
          file: null,
        }
      : {
          name: '',
          currency: convertForSelect(ApiOrganisationCurrencyEnum.EUR),
          source: SOURCE_EMPTY,
          file: null,
        };

  const initialFormikState: Partial<FormikState<OrganisationModalValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const onDeactivateOrganisation = async (organisation: ApiOrganisation) => {
    if (organisation.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'This organisation will be deactivated.',
          confirmationText: 'Yes, deactivate the organisation!',
          cancelText: 'No, keep the organisation active',
          onConfirm: async () => {
            await deactivateOrganisation(organisation.id!);
            await onChange();
            setShowModal(false);
          },
        })
      );
    }
  };
  const onActivateOrganisation = async (organisation: ApiOrganisation) => {
    if (organisation.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'This organisation will be reactivated.',
          confirmationText: 'Yes, reactivate the organisation!',
          cancelText: 'No, keep it deactivated',
          onConfirm: async () => {
            await activateOrganisation(organisation.id!);
            await onChange();
            setShowModal(false);
          },
        })
      );
    }
  };

  const submit = async (
    values: OrganisationModalValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<OrganisationModalValues>
  ) => {
    try {
      if (values.source === SOURCE_IMPORT && !values.id) {
        const confirmed = await dispatch(
          showConfirmationModal({
            title: 'Are you sure?',
            description: (
              <>
                <strong>{values.name.trim()}</strong> will be created and populated from{' '}
                <strong>{values.file?.name}</strong>. This restores the source organisation&apos;s foundation
                configuration, schemas, custom lists, query templates, risk rules, scenarios and alert decision rules,
                screening config, alert handling, and attribute mappings. Customers, alerts, transactions, users, and
                secrets are not in the bundle and must be configured separately. Creating a new organisation might
                introduce short downtime. Make sure it is done outside of peak working hours or has been coordinated
                with engineers.
              </>
            ),
            confirmationText: 'Yes, create & import!',
            cancelText: 'No, will do it later',
          })
        );
        if (!confirmed) {
          setSubmitting(false);
          return;
        }
        await dispatch(
          importOrganisationConfig(
            values.file as File,
            values.name.trim(),
            values.currency.value as ApiOrganisationCurrencyEnum
          )
        );
        // Import already succeeded server-side; close the modal so a refetch failure cannot
        // trap the user into retrying and hitting a duplicate-name 422.
        setShowModal(false);
        // Refresh the org switcher so the new org appears without a manual reload.
        queryClient.invalidateQueries({ queryKey: ['me'] });
        try {
          await onChange();
        } catch {
          // Swallow refetch errors — the new organisation exists; user can reload to see it.
        }
        resetForm(initialFormikState);
        setSubmitting(false);
        return;
      } else if (!values.id) {
        const confirmed = await dispatch(
          showConfirmationModal({
            title: 'Are you sure?',
            description:
              'Currently creating new organisation might introduce short downtime. Make sure it is done outside of' +
              ' peak working hours or you have coordinated it with engineers.',
            confirmationText: 'Yes, create!',
            cancelText: 'No, will do it later',
          })
        );
        if (!confirmed) {
          setSubmitting(false);
          return;
        }
        await onChange(values);
      } else {
        await onChange(values);
      }
      setShowModal(false);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
      setSubmitting(false);
      return;
    }
    resetForm(initialFormikState);
    setSubmitting(false);
  };

  return (
    <Formik onSubmit={submit} validationSchema={validationSchema} initialValues={initialValues}>
      {({ isSubmitting, values, setFieldValue, status }: FormikProps<OrganisationModalValues>) => {
        return (
          <React.Fragment>
            {(organisation && (
              <SalvButton label={'Edit'} variant='muted' size={Size.EXTRA_SMALL} onClick={() => setShowModal(true)} />
            )) || (
              <SalvButton
                label={'Add new organisation'}
                icon={faPlus}
                variant='primary'
                size={Size.SMALL}
                onClick={() => setShowModal(true)}
              />
            )}
            <SalvModal show={showModal} onHide={() => setShowModal(false)}>
              <div className='organisation-modal'>
                <Form>
                  <SalvModal.Header>
                    <div>{organisation ? 'Edit organisation' : 'New organisation'}</div>
                  </SalvModal.Header>

                  <SalvModal.Body>
                    {status && (
                      <Row>
                        <Col md={12}>
                          <Alert variant='danger'>{status}</Alert>
                        </Col>
                      </Row>
                    )}
                    <div className='form-grid-row'>
                      <FormField name='name' type='text' label='Name' placeholder='e.g. Acme Payments Ltd' />
                      <FormField name='currency' options={currencyType} type='select' label='Default currency' />
                    </div>
                    {!organisation && (
                      <>
                        <SalvRadioGroup
                          label='Starting configuration'
                          required={true}
                          size={Size.MEDIUM}
                          value={values.source}
                          onChange={(value) => {
                            const nextSource = value as OrganisationModalSource;
                            setFieldValue('source', nextSource);
                            if (nextSource !== SOURCE_IMPORT) {
                              setFieldValue('file', null);
                            }
                          }}
                        >
                          <div className='source-card-grid'>
                            {SOURCE_OPTIONS.map((option) => (
                              <div
                                key={option.value}
                                className={`source-card ${values.source === option.value ? 'selected' : ''}`}
                                onClick={() => {
                                  setFieldValue('source', option.value);
                                  if (option.value !== SOURCE_IMPORT) {
                                    setFieldValue('file', null);
                                  }
                                }}
                              >
                                <SalvRadio value={option.value} label={option.title} />
                                <div className='source-body'>
                                  <div className='source-title'>
                                    <FontAwesomeIcon icon={option.icon} className='source-title-icon' />
                                    {option.title}
                                  </div>
                                  <div className='source-description'>{option.description}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </SalvRadioGroup>
                        {values.source === SOURCE_IMPORT && (
                          <div className='mt-3'>
                            <FileUpload
                              size={Size.MEDIUM}
                              isMulti={false}
                              accept={{ 'application/json': ['.json'] }}
                              externallyUpdatedFiles={values.file ? [values.file] : []}
                              onChange={(files) => setFieldValue('file', files[0] ?? null)}
                              hideUploadedFile={false}
                            >
                              <div className='bundle-dropzone'>
                                <div className='bundle-dropzone-icon'>
                                  <FontAwesomeIcon icon={faFileArrowUp} />
                                </div>
                                <h4 className='bundle-dropzone-title'>Drop a .json bundle here, or click to select</h4>
                                <p className='bundle-dropzone-hint'>Bundles are verified on upload.</p>
                              </div>
                            </FileUpload>
                          </div>
                        )}
                      </>
                    )}
                  </SalvModal.Body>

                  <SalvModal.Footer>
                    {organisation && !organisation.inactive && (
                      <SalvButton
                        disabled={isSubmitting}
                        onClick={() => onDeactivateOrganisation(organisation)}
                        variant='danger-outline'
                        label='Deactivate'
                      />
                    )}
                    {organisation && organisation.inactive && (
                      <SalvButton
                        disabled={isSubmitting}
                        onClick={() => onActivateOrganisation(organisation)}
                        variant='danger-outline'
                        label='Reactivate'
                      />
                    )}
                    <SalvButton
                      variant='secondary'
                      label='Close'
                      onClick={() => setShowModal(false)}
                      disabled={isSubmitting}
                    />
                    <SalvButton
                      disabled={isSubmitting || (!organisation && !canCreate(values))}
                      type='submit'
                      variant='primary'
                      icon={organisation ? undefined : faPlus}
                      label={
                        organisation
                          ? 'Save'
                          : values.source === SOURCE_IMPORT
                            ? 'Create organisation & import'
                            : values.source === SOURCE_DEMO
                              ? 'Create organisation from preset'
                              : 'Create organisation'
                      }
                      onClick={() => {}}
                    />
                  </SalvModal.Footer>
                </Form>
              </div>
            </SalvModal>
          </React.Fragment>
        );
      }}
    </Formik>
  );
};

export default OrganisationModal;
